import { h, defineComponent, ref, computed, Ref } from 'vue';
import { t } from '@/ui/i18n/i18n';
import { box, apiErrorMessage, loadingButton, loader } from '@/ui/components';
import { useTask, useValidation } from '@/ui/concerns';
import { updateNotificationSettings, getNotificationSettings } from '@/services/api/auth';
import { updateUserNotificationSettings, getUserNotificationSettings } from '@/services/api/resources/user';
import { ReminderConfiguration, validate as validateConfiguration, PageTypes as PageType } from '@/entities/auth/reminderConfiguration';
import { inputField } from '@/ui/components/form';
import { toasted } from '@/ui/helpers';
import { isOnFreePlan, user } from '@/ui/auth/auth';
import { UpgradeAd } from '@/ui/components/upgrade/UpgradeAd';
import { ConfigurationTable } from './ConfigurationTable';
import { TimeWindowModal } from '@/ui/components/reminderNotification/TimeWindowModal';
import { IdProps } from '@/ui/helpers/uiHelper';

interface ReminderConfigurationsProps extends IdProps{
  pageType: PageType;
}

export const ReminderConfigurations = defineComponent<ReminderConfigurationsProps>({
  name: 'ReminderConfigurations',
  props: ['id', 'pageType'],
  setup({ id, pageType }) {
    const configuration = ref<ReminderConfiguration>({
      notificationEmail: '',
      phoneNumber: '',
      notificationSettings: [],
    });
    const isModalOpen = ref(false);
    let recordVersion = '';

    const formNotificationSettings = computed(() => configuration.value.notificationSettings.filter(setting => !setting.data.type.includes('issue')));
    const issueNotificationSettings = computed(() => configuration.value.notificationSettings.filter(setting => setting.data.type.includes('issue')));

    const openModal = () => {
      isModalOpen.value = true;
    };

    const closeModal = (r: Ref<boolean>) => {
      return () => {
        r.value = false;
      };
    };

    const getConfigurationTask = useTask(
      () => getNotificationSettings(),
      response => {
        configuration.value = response.data;
      },
    );

    const getUserConfigurationTask = useTask(
      () => getUserNotificationSettings(id),
      ({ data, version }) => {
        configuration.value = data.data;
        recordVersion = version;
      },
    );

    const {
      run: updateConfigurationTask,
      state: updateConfigurationsState,
    } = useTask(
      () => id ? updateUserNotificationSettings(configuration.value, id, recordVersion)
        : updateNotificationSettings(configuration.value),
      () => {
        toasted.success(t('profile.notifications-updated'));
      },
    );

    const {
      validationErrors: configurationValidationErrors,
      hasNoValidationErrors: configurationHasNoValidationErrors,
      showValidationErrors: configurationShowValidationErrors,
    } = useValidation(validateConfiguration, configuration, updateConfigurationsState);

    const handleConfigurationUpdate = () => {
      configurationShowValidationErrors.value = true;
      if (configurationHasNoValidationErrors.value) {
        updateConfigurationTask();
      }
    };

    return function render() {
      return h('div', { class: 'reminder-configurations' }, [
        box([loader('form', id ? getUserConfigurationTask : getConfigurationTask, [
          isOnFreePlan.value ? h(UpgradeAd, {
            class: 'upgrade-ad--reminder-notification',
            props: {
              message: t('upgrade-ad.reminder-notification'),
              isVertical: true,
            },
          })
            : [
              inputField({
                name: 'notificationEmail',
                type: 'email',
                label: t('profile.email-for-notifications'),
                placeholder: t('profile.enter-email-notifications'),
                value: configuration.value.notificationEmail,
                required: !!configuration.value.notificationSettings.some(settings => settings.data.email),
                errors: configurationValidationErrors.value,
                disabled: updateConfigurationsState.value.pending,
              }, v => {
                configuration.value.notificationEmail = v;
              }),

              user.value.organization.smsServiceEnabled && inputField({
                name: 'phoneNumber',
                type: 'text',
                label: t('profile.phone-notifications'),
                placeholder: t('+1234567890'),
                value: configuration.value.phoneNumber,
                required: !!configuration.value.notificationSettings.some(settings => settings.data.sms),
                errors: configurationValidationErrors.value,
                disabled: updateConfigurationsState.value.pending,
              }, v => {
                configuration.value.phoneNumber = v;
              }),
            ],

          h(ConfigurationTable, {
            props: {
              list: formNotificationSettings.value,
              pageType: pageType || 'profile',
            },
          }),

          h(ConfigurationTable, {
            props: {
              list: issueNotificationSettings.value,
              pageType: pageType || 'profile',
            },
            on: {
              openModal,
            },
          }),

          apiErrorMessage(updateConfigurationsState.value),

          loadingButton(
            t('reminder-notification.save'),
            handleConfigurationUpdate,
            updateConfigurationsState.value.pending,
          ),

          h(TimeWindowModal, {
            props: {
              active: isModalOpen,
            },
            on: {
              close: closeModal(isModalOpen),
            },
          }),
        ])]),
      ]);
    };
  },
});
